import AxiosInstance from '@/api/axios';
import {
  defaultPagination,
  PaginationInterface,
} from '@/components/Base/Pagination';
import { IBranches } from '@/pages/Branches';
import { IRegion } from '@/pages/Settings/RegionSettingsForm';
import { ROLE } from '@/utils/enum';
import { getClientId } from '@/utils/helper';
import { ClientFilterType } from '@/utils/types';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

interface IClientTeamFilter extends ClientFilterType {
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
}

export const fetchClientTeams = createAsyncThunk(
  'fetchClientTeams/fetchData',
  async (
    { currentPage, perpage, search, sortField, sortOrder }: IClientTeamFilter,
    thunkAPI
  ) => {
    try {
       const organization_id = getClientId();
      const response = await AxiosInstance.get(
        `/client-teams/${organization_id}`,
        {
          params: {
            page: currentPage,
            perpage: perpage,
            search: search,
            sortField: sortField,
            sortOrder: sortOrder,
          },
        }
      );
      if (response.data.success) {
        return response.data.data;
      } else {
        return null;
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

interface ClientLoginsType {
  user: string;
  lastLogin?: string;
}

export interface ClientTeamsType extends ClientLoginsType {
  _id: string;
  name: string;
  email: string;
  photo: string;
  phone: string;
  status: string;
  branches: IBranches[];
  regions: IRegion[];
  role: ROLE;
  /**
   * Populated dynamic role. `role` above is the legacy mirror and reads USER
   * for every custom role, so display this.
   */
  role_id?: { _id: string; name: string; isSystem: boolean } | string | null;
  media?: {
    _id: string;
    path?: string;
  };
  twoFA: boolean;
  createdAt: string;
  updatedAt: string;
  resetPassword: string;
  lock: string;
}

interface ClientTeamsStateType {
  clientTeams: {
    data: ClientTeamsType[] | null | undefined;
    pagination: PaginationInterface;
    clientLogins: ClientLoginsType[];
    sortField: string;
    sortOrder: 'asc' | 'desc';
  };
  loading: boolean;
  error: string | null;
}

const initialState: ClientTeamsStateType = {
  clientTeams: {
    data: null,
    pagination: defaultPagination,
    sortField: '',
    sortOrder: 'asc',
    clientLogins: [],
  },
  loading: false,
  error: null,
};

const clientTeamsReducer = createSlice({
  name: 'clientTeams',
  initialState,
  reducers: {
    updatePagination: (state, action) => {
      state.clientTeams.pagination = {
        ...state.clientTeams.pagination,
        ...action.payload, // Update pagination properties based on action payload
      };
    },

    updateData: (state, action) => {
      if (Array.isArray(action.payload)) {
        state.clientTeams.data = action.payload;
      } else {
        console.error('Payload must be an array!');
      }
    },

    updateSorting: (state, action) => {
      state.clientTeams.sortField = action.payload.sortField;
      state.clientTeams.sortOrder = action.payload.sortOrder;
      state.clientTeams.data = action.payload.sortedData;
    },

    unlockTeamData: (state, action) => {
      if (Array.isArray(action.payload)) {
        state.clientTeams.data = action.payload;
      } else {
        console.error('Payload must be an array!');
      }
    },

    updateClientEdit: (state, action) => {
      if (Array.isArray(state.clientTeams.data)) {
        const index = state.clientTeams.data.findIndex(
          (item) => item._id === action.payload._id
        );

        if (index !== -1) {
          state.clientTeams.data = [
            ...state.clientTeams.data.slice(0, index),
            action.payload,
            ...state.clientTeams.data.slice(index + 1),
          ];
        }
      } else {
        console.error('clientTeams is not an array!');
      }
    },
    deleteClientImage: (state, action) => {
      if (Array.isArray(state.clientTeams.data)) {
        const index = state.clientTeams.data.findIndex(
          (item) => item._id === action.payload
        );

        if (index !== -1) {
          state.clientTeams.data[index].media = undefined;
        } else {
          console.warn('Client not found with ID:', action.payload);
        }
      } else {
        console.error('clientTeams is not an array!');
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientTeams.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClientTeams.fulfilled, (state, action) => {
        state.loading = false;
        state.clientTeams = action.payload;
      })
      .addCase(fetchClientTeams.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  updatePagination,
  updateData,
  updateClientEdit,
  unlockTeamData,
  updateSorting,
  deleteClientImage,
} = clientTeamsReducer.actions;

export default clientTeamsReducer.reducer;
